<script setup lang="ts">
import {ref} from "vue"


type ProgressReport = {
  gradeGain: number
  weeks: number
  rows: [string, string, string][]
}

type Video = {
  name: string
  title: string
  caption?: string
  report?: ProgressReport
}

const INTERVIEWS: Video[] = [
  {
    name: 'ashley_and_alexis_interview',
    title: "Ashley and Alexis",
    report: {
      gradeGain: 2,
      weeks: 20,
      rows: [
        ["Phonics", "62% Mastery", "97% Mastery"],
        ["Fluency Correct Words/Min", "90 WPM at 3rd Grade", "115 WPM at 5th Grade"],
        ["Comprehension Level", "55% at 4th Grade", "93% at 5th grade"],
      ]
    }
  },
  {
    name: 'aurelie_testimonial',
    title: "Aurelie and Juliette",
    report: {
      gradeGain: 2,
      weeks: 36,
      rows: [
        ["Phonics", "76% Mastery", "100% Mastery"],
        ["Fluency Correct Words/Min", "70 WPM at 2nd Grade", "115 WPM at 4th Grade"],
        ["Comprehension Level", "80% at 4th Grade", "90% at 4th grade"],
      ]
    }
  },
  {
    name: 'candy_and_aiden_interview',
    title: "Candy and Aiden",
    report: {
      gradeGain: 1.5,
      weeks: 24,
      rows: [
        ["Phonics", "84% Mastery", "94% Mastery"],
        ["Fluency Correct Words/Min", "72 WPM at 2nd Grade", "78 WPM at 5th Grade"],
        ["Comprehension Level", "73% at 3rd Grade", "84% at 4th grade"],
      ]
    }
  },
  {
    name: 'caulder_and_tammy',
    title: "Caulder and Tammy",
    report: {
      gradeGain: 2.5,
      weeks: 48,
      rows: [
        ["Phonics", "51% Mastery", "93% Mastery"],
        ["Fluency Correct Words/Min", "21 WPM at 1st Grade", "117 WPM at 3rd Grade"],
        ["Comprehension Level", "90% at 1st Grade", "100% at 3rd grade"],
      ]
    }
  },
  {
    name: 'chelsea_testimonial',
    title: "Chelsie and Nash",
    report: {
      gradeGain: 3,
      weeks: 48,
      rows: [
        ["Phonics", "71% Mastery", "100% Mastery"],
        ["Fluency Correct Words/Min", "42 WPM at 1st Grade", "74 WPM at 4th Grade"],
        ["Comprehension Level", "89% at 1st Grade", "84% at 4th grade"],
      ]
    }
  },
  {
    name: 'danielle_and_grace_interview',
    title: "Danielle and Grace",
    report: {
      gradeGain: 2,
      weeks: 36,
      rows: [
        ["Phonics", "69% Mastery", "98% Mastery"],
        ["Fluency Correct Words/Min", "52 WPM at 1st Grade", "67 WPM at 3rd Grade"],
        ["Comprehension Level", "100% at 1st Grade", "80% at 3rd grade"],
      ]
    }
  },
  {
    name: 'greg_and_rosa',
    title: "Greg and Rosa",
    report: {
      gradeGain: 2,
      weeks: 24,
      rows: [
        ["Phonics", "43% Mastery", "81% Mastery"],
        ["Fluency Correct Words/Min", "4 WPM at 1st Grade", "44 WPM at 2nd Grade"],
        ["Comprehension Level", "15% at 2nd Grade", "80% at 2nd grade"],
      ]
    }
  },
  {
    name: 'isa_testimonial',
    title: "Interview with Isa",
    caption: "Isa was a 1st grader from Guam, and one of the 1st students outside of Mr. Spartz’s class to use the program. We had to include her video interview because she’s amazing and embodies exactly the increase in confidence we try to instill in our kids. She went from Unsatisfactory to Proficient in 5 months.",
  },
  {
    name: 'jessie_testimonial',
    title: "Jessie and Ashor",
    report: {
      gradeGain: 1,
      weeks: 12,
      rows: [
        ["Phonics", "95% Mastery", "98% Mastery"],
        ["Fluency Correct Words/Min", "99 WPM at 5th Grade", "118 WPM at 5th Grade"],
        ["Comprehension Level", "80% at 4th Grade", "86% at 5th grade"],
      ]
    }
  },
  {
    name: 'jill_brock_testimonial',
    title: "Jill and Brock",
    report: {
      gradeGain: 3.5,
      weeks: 64,
      rows: [
        ["Phonics", "63% Mastery", "99% Mastery"],
        ["Fluency Correct Words/Min", "67 WPM at 2nd Grade", "80 WPM at 5th Grade"],
        ["Comprehension Level", "50% at 2nd Grade", "86% at 5th grade"],
      ]
    }
  },
  {
    name: 'joanna_and_christian_interview',
    title: "Joanna and Christian",
    report: {
      gradeGain: 1.5,
      weeks: 24,
      rows: [
        ["Phonics", "49% Mastery", "83% Mastery"],
        ["Fluency Correct Words/Min", "3 WPM at 1st Grade", "26 WPM at 1st Grade"],
        ["Comprehension Level", "Early Kindergarten", "80% at 1st grade"],
      ]
    }
  },
  {
    name: 'kara_and_hanalei',
    title: "Kara and Hanalie",
    report: {
      gradeGain: 1,
      weeks: 12,
      rows: [
        ["Phonics", "62% Mastery", "87% Mastery"],
        ["Fluency Correct Words/Min", "31 WPM at 2nd Grade", "71 WPM at 2nd Grade"],
        ["Comprehension Level", "89% at 1st Grade", "88% at 2nd grade"],
      ]
    }
  },
  {
    name: 'luke_and_lorranda_interview',
    title: "Luke and Lorranda",
    report: {
      gradeGain: 1.5,
      weeks: 24,
      rows: [
        ["Phonics", "60% Mastery", "91% Mastery"],
        ["Fluency Correct Words/Min", "45 WPM at 3rd Grade", "80 WPM at 3rd Grade"],
        ["Comprehension Level", "70% at 2nd Grade", "95% at 3rd grade"],
      ]
    }
  },
  {
    name: 'melissa_and_tye_interview',
    title: "Melissa and Tye",
    report: {
      gradeGain: 2.5,
      weeks: 24,
      rows: [
        ["Phonics", "73% Mastery", "99% Mastery"],
        ["Fluency Correct Words/Min", "76 WPM at 3rd Grade", "95 WPM at 5th Grade"],
        ["Comprehension Level", "30% at 5th Grade", "86% at 5th grade"],
      ]
    }
  },
  {
    name: 'peri_and_wendy_interview',
    title: "Peri and Wendy",
    report: {
      gradeGain: 3,
      weeks: 60,
      rows: [
        ["Phonics", "88% Mastery", "100% Mastery"],
        ["Fluency Correct Words/Min", "35 WPM at 2nd Grade", "95 WPM at 5th Grade"],
        ["Comprehension Level", "30% at 5th Grade", "86% at 5th grade"],
      ]
    }
  },
  {
    name: 'tashitha_and_greyson',
    title: "Tashitha and Greyson",
    report: {
      gradeGain: 2.5,
      weeks: 24,
      rows: [
        ["Phonics", "48% Mastery", "82% Mastery"],
        ["Fluency Correct Words/Min", "8 WPM at 2nd Grade", "74 WPM at 2nd Grade"],
        ["Comprehension Level", "0% at 2nd Grade", "70% at 2nd Grade"],
      ]
    }
  },
  {
    name: 'turqoise_and_londyn',
    title: "Turquoise and Londyn",
    report: {
      gradeGain: 1.5,
      weeks: 24,
      rows: [
        ["Phonics", "94% Mastery", "100% Mastery"],
        ["Fluency Correct Words/Min", "93 WPM at 3rd Grade", "124 WPM at 3rd Grade"],
        ["Comprehension Level", "62% at 3rd Grade", "92% at 4th grade"],
      ]
    }
  },
  {
    // using lefty for the icon
    name: 'mr_spartz_class_testimonials',
    title: "More from Mr. Spartz's class",
    caption: "These 4th-graders were in Jeremy’s 1st Lyrics2Learn Pilot! Each student closed the gap in both fluency and comprehension, making reading gains that exceeded the national average for 4th-grade growth (NWEA) by 50% to 300%.",
  },
  {
    // can't get a good thumbnail off this one, using l2l logo
    name: 'caleb_and_alicia',
    title: "Caleb and Alicia",
    report: {
      gradeGain: 2,
      weeks: 24,
      rows: [
        ["Phonics", "71% Mastery", "90% Mastery"],
        ["Fluency Correct Words/Min", "40 WPM at 3rd Grade", "100 WPM at 3rd Grade"],
        ["Comprehension Level", "28% at 4th Grade", "88% at 4th grade"],
      ]
    }
  },
];

const showModal = ref(false);

const selectedVideo = ref<Video | null>(null);

function selectVideo(video: Video): void {
  selectedVideo.value = video;
  showModal.value = true
}

function videoTitle(video: Video) {
  const gain = video.report?.gradeGain ?? 0
  const gainLabel = gain ? `: +${gain} grade level` : ''
  const s = gain > 1 ? 's' : ''
  return video.title + gainLabel + s
}
</script>

<template>
  <div class="testimonial-videos">
    <div class="thumbnail-grid">
      <div
        v-for="video in INTERVIEWS"
        :key="video.name"
        class="thumbnail-card"
        role="button"
        tabindex="0"
        @click="selectVideo(video)"
        @keydown.enter.prevent="selectVideo(video)"
        @keydown.space.prevent="selectVideo(video)"
      >
        <div class="thumbnail-container">
          <img class="thumbnail" :src="`/images/testimonials/${video.name}.png`" :alt="video.title"/>
          <div class="play-button-overlay"> ▷</div>
        </div>
        <strong>{{ videoTitle(video) }}</strong>

        <table v-if="video.report" class="report-card">
          <thead>
          <tr>
            <th class="category corner"></th>
            <th class="week-1">Week 1</th>
            <th class="week-n">Week {{ video.report.weeks }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in video.report.rows" :key="row[0]">
            <th class="category">{{ row[0] }}</th>
            <td class="week-1">{{ row[1] }}</td>
            <td class="week-n">{{ row[2] }}</td>
          </tr>
          </tbody>
        </table>
        <p>{{ video.caption }}</p>
      </div>
    </div>

    <b-modal v-model="showModal" class="video-player-modal" size="xl" centered hide-footer>
      <div class="video-player-frame">
        <div class="video"> <!-- need this intermediate div for $reasons -->
          <video controls autoplay>
            <source type="video/mp4" :src="`/tutor/interview/${selectedVideo?.name}`"/>
          </video>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style scoped lang="scss">
@import "../../sass/theme";

.interviews-page {
}

.thumbnail-grid {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}

.thumbnail-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  width: 350px;
  margin: 1rem;
  padding: 1rem;
  max-height: 600px;
  border-radius: 5px;
  border: 2px #ccc solid;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 5px 5px 10px #555e;
  }
}

.report-card {
  font-size: 0.75rem;
  margin-top: 1rem;

  thead {
    white-space: nowrap;

    th {
      text-align: center;
    }
  }

  tbody th {
    text-align: center;
    background-color: lighten($cs-l2l-light-blue, 8%);
  }

  tbody {
    th, td {
      border: 1px solid #ccc;
      padding: 3px;
    }

    .week-1 {
      background-color: lighten($cs-l2l-light-purple, 7%);
    }

    .week-n {
      background-color: $cs-l2l-light-yellow;
    }
  }
}

.thumbnail-container {
  position: relative;
}

.thumbnail {
  max-width: 12rem;
  border-radius: 10px;
  border: 1px solid #eee;
}

.play-button-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: #ccca;
  cursor: pointer;
}

.video-player-modal {
  width: 100%;
  height: 100%;
}

.video-player-frame {
  max-width: 100% !important;
  max-height: 100% !important;

  display: flex;
  align-items: center;
  justify-content: center;
}

video {
  margin: auto;
  width: 100%;
  height: 100%;
}

</style>
